import type { AdapterRecord } from "@repo/types/adapter";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/card";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/select";
import { Textarea } from "@repo/ui/textarea";
import { Loader2, Save } from "lucide-react";
import { useMemo, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { updateAdapter } from "@/lib/api";

const ALL_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const STATUS_OPTIONS: Array<{ value: AdapterRecord["status"]; label: string }> = [
  { value: "active", label: "Active" },
  { value: "development", label: "Development" },
  { value: "disabled", label: "Disabled" },
];

interface AdapterParametersFormProps {
  adapter: AdapterRecord;
  onSaved?: (next: AdapterRecord) => void;
}

/**
 * Inline editor for mutable adapter columns on the `adapters` table.
 * Persists through the `updateAdapter` API helper.
 */
export function AdapterParametersForm({ adapter, onSaved }: AdapterParametersFormProps) {
  const { toast } = useToast();
  const [status, setStatus] = useState<AdapterRecord["status"]>(adapter.status);
  const [description, setDescription] = useState<string>(adapter.description ?? "");
  const [supportedStates, setSupportedStates] = useState<string[]>(
    adapter.supported_states ?? [],
  );
  const [defaultConfigText, setDefaultConfigText] = useState<string>(() => {
    try {
      return JSON.stringify(adapter.default_config ?? {}, null, 2);
    } catch {
      return "{}";
    }
  });
  const [isSaving, setIsSaving] = useState(false);

  const parsedDefaultConfig = useMemo((): Record<string, unknown> | null => {
    try {
      const raw = JSON.parse(defaultConfigText || "{}");
      if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
        return null;
      }
      return raw as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [defaultConfigText]);

  const toggleState = (state: string) => {
    setSupportedStates((prev) =>
      prev.includes(state) ? prev.filter((s) => s !== state) : [...prev, state],
    );
  };

  const handleSave = async () => {
    if (parsedDefaultConfig === null) {
      toast({
        title: "Invalid default config",
        description: "Default config must be a JSON object.",
        variant: "destructive",
      });
      return;
    }
    setIsSaving(true);
    const result = await updateAdapter(adapter.id, {
      status,
      description: description.trim() ? description.trim() : null,
      supported_states: supportedStates.length > 0 ? supportedStates : null,
      default_config: parsedDefaultConfig,
    });
    setIsSaving(false);
    if (!result) {
      toast({
        title: "Save failed",
        description: "Could not update adapter parameters.",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Adapter updated", description: "Parameters saved." });
    onSaved?.(result);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Adapter parameters</CardTitle>
        <CardDescription>
          Editable columns on the <code className="text-xs">adapters</code> table.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="adapter-status">Status</Label>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as AdapterRecord["status"])}
            >
              <SelectTrigger id="adapter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adapter-name">Name</Label>
            <Input
              id="adapter-name"
              name="adapter-name"
              value={adapter.name}
              readOnly
              className="bg-muted/40"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Supported states</Label>
          <div className="flex flex-wrap gap-2">
            {ALL_STATES.map((state) => {
              const active = supportedStates.includes(state);
              return (
                <Badge
                  key={state}
                  variant={active ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleState(state)}
                >
                  {state}
                </Badge>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            Leave empty to indicate the adapter supports all states.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="adapter-description">Description</Label>
          <Textarea
            id="adapter-description"
            name="adapter-description"
            value={description}
            rows={3}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Operator-facing summary of this adapter."
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="adapter-default-config">Default config (JSON object)</Label>
          <Textarea
            id="adapter-default-config"
            name="adapter-default-config"
            value={defaultConfigText}
            rows={8}
            onChange={(event) => setDefaultConfigText(event.target.value)}
            className="font-mono text-xs"
          />
          {parsedDefaultConfig === null && (
            <p className="text-xs text-destructive">
              Invalid JSON object — fix before saving.
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving || parsedDefaultConfig === null}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save parameters
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
